import { assertGraphCapture } from '@/simulation/native/authority';
import type { NodeHandle } from '@/models/logic/RuntimeHandles';
import type { IChipInstanceModel, ILogicGraph } from '@/interfaces/models';
import type { TileInstance } from '@/models/tiles/TileInstance';
import type { LogicGraphDto } from '@/schemas/logic';
import { deepClone } from './clone';
import { LogicGraph } from '@/models/logic/LogicGraph';
import { nodeSnapshotWallAt, withNodeSnapshotTime } from '@/models/logic/SavedNodeState';

interface OwnershipPlan {
  revision: number;
  children: { graph: LogicGraph; revision: number }[];
  authored: Set<NodeHandle>;
  hadDuplicates: boolean;
  retained: NodeHandle[];
}
// Only membership is reused. Every save still captures live node state and edges.
const ownershipPlans = new WeakMap<LogicGraph, OwnershipPlan>();
const trackedGraph = (graph: ILogicGraph): graph is LogicGraph =>
  Object.getPrototypeOf(graph) === LogicGraph.prototype &&
  (graph as LogicGraph).isTopologyTracked;

function authoredReferences(tiles: readonly TileInstance[]): Set<NodeHandle> {
  const references = new Set<NodeHandle>();
  for (const tile of tiles) {
    if (tile.meta.partOfChip) continue;
    for (const id of [
      ...Object.values(tile.logicNodeIds ?? {}),
      ...Object.values(tile.laneNodeIds ?? {}).flat(),
    ])
      if (id) references.add(id);
  }
  return references;
}

function captureRetained(graph: ILogicGraph, ids: readonly NodeHandle[]): LogicGraphDto {
  const retained = new Set(ids);
  const nodes = withNodeSnapshotTime(nodeSnapshotWallAt(), () =>
    ids.map((id) => graph.nodes.get(id)!.dto),
  );
  const edges: LogicGraphDto['edges'] = [];
  if (retained.size)
    for (const edge of graph.edges.values())
      if (retained.has(edge.sourceNodeId) && retained.has(edge.targetNodeId))
        edges.push(graph.edgeDto(edge));
  return { nodes, edges };
}

/** Child snapshots already own the same live nodes merged into their parent.
 * Keep parent-only state and canonical nodes referenced by authored parent tiles;
 * retain a full parent record when its child record is a different live object.
 * Child snapshots remain complete for the existing runtime restoration path. */
export function ownerRuntimeSnapshot(
  graph: ILogicGraph,
  children: Iterable<IChipInstanceModel>,
  tiles: readonly TileInstance[],
): LogicGraphDto {
  assertGraphCapture(graph);
  const ids = ownerRuntimeNodeIds(graph, children, tiles);
  return ids ? captureRetained(graph, ids) : graph.dto;
}

/** Membership only; capture callers can stream live records in the same turn. */
export function ownerRuntimeNodeIds(
  graph: ILogicGraph,
  children: Iterable<IChipInstanceModel>,
  tiles: readonly TileInstance[],
): readonly NodeHandle[] | undefined {
  // Graph subclasses and externally supplied maps have no native revision proof.
  const childGraphs = Array.from(children, (child) => child.logicGraph);
  if (!childGraphs.length) {
    ownershipPlans.delete(graph as LogicGraph);
    return undefined;
  }
  const cacheable = trackedGraph(graph) && childGraphs.every(trackedGraph);
  if (!cacheable) ownershipPlans.delete(graph as LogicGraph);
  const authored = authoredReferences(tiles);
  if (cacheable) {
    const plan = ownershipPlans.get(graph);
    if (
      plan &&
      plan.revision === graph.topologyRevision &&
      plan.children.length === childGraphs.length &&
      plan.children.every(
        (child, i) =>
          child.graph === childGraphs[i] &&
          child.revision === (childGraphs[i] as LogicGraph).topologyRevision,
      ) &&
      plan.authored.size === authored.size &&
      [...authored].every((id) => plan.authored.has(id))
    )
      return plan.hadDuplicates ? plan.retained : undefined;
  }
  const duplicated = new Set<NodeHandle>();
  for (const child of childGraphs)
    for (const [id, node] of child.nodes)
      if (graph.nodes.get(id) === node) duplicated.add(id);
  const hadDuplicates = duplicated.size > 0;
  for (const id of authored) duplicated.delete(id);
  const retained: NodeHandle[] = [];
  if (hadDuplicates)
    for (const id of graph.nodes.keys()) if (!duplicated.has(id)) retained.push(id);
  if (cacheable) {
    ownershipPlans.set(graph, {
      revision: graph.topologyRevision,
      children: childGraphs.map((child) => ({
        graph: child as LogicGraph,
        revision: (child as LogicGraph).topologyRevision,
      })),
      authored,
      hadDuplicates,
      retained,
    });
  }
  return hadDuplicates ? retained : undefined;
}
